import { useState, useCallback } from 'react';
import {
  Banner,
  Button,
  Modal,
  Text,
  BlockStack,
  InlineStack,
  List,
  Badge,
  Card,
  Box,
  Divider
} from '@shopify/polaris';

function SubscriptionBanner({ onSubscribe, onStatusChange }) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Shopify Billing flow: create subscription -> open confirmation URL
  const handleSubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/subscription/billing/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.confirmationUrl) {
        // In embedded context, navigate the TOP frame to the confirmation screen.
        try {
          window.open(data.confirmationUrl, '_top');
        } catch (e) {
          window.top.location.href = data.confirmationUrl;
        }
      } else {
        throw new Error(data.error || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Standalone/mock activation (used in non-embedded previews)
  const handleMockSubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/subscription/mock-activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        if (onStatusChange) onStatusChange(data.subscription);
        setShowModal(false);
      }
    } catch (error) {
      console.error('Mock subscription error:', error);
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  const isStandalone = window.location.hostname === 'localhost' || !window.shopify;

  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <Banner
          title="Unlock Full Features"
          tone="warning"
          action={{ content: 'View Plans', onAction: () => setShowModal(true) }}
        >
          <p>
            You're using the free demo. Subscribe for $9/month to unlock bulk printing, 
            all printer presets, and custom templates.
          </p>
        </Banner>
      </div>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="ProLabel Wizard Subscription"
        primaryAction={{
          content: 'Subscribe - $9/month',
          onAction: handleSubscribe,
          loading
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowModal(false) },
          // Standalone mode gets a free activation for previews/tests
          ...(isStandalone ? [{
            content: '(Dev) Activate Free',
            onAction: handleMockSubscribe
          }] : [])
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {/* Free Tier */}
            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <InlineStack gap="200" align="space-between">
                    <Text variant="headingMd">Free Demo</Text>
                    <Badge>Current Plan</Badge>
                  </InlineStack>
                  <List>
                    <List.Item>1 free label print</List.Item>
                    <List.Item>Preview all features</List.Item>
                    <List.Item>Basic presets only</List.Item>
                  </List>
                </BlockStack>
              </Box>
            </Card>

            {/* Pro Plan */}
            <Card>
              <Box padding="400" background="bg-surface-success">
                <BlockStack gap="300">
                  <InlineStack gap="200" align="space-between">
                    <Text variant="headingMd">Pro Monthly</Text>
                    <Badge tone="success">Recommended</Badge>
                  </InlineStack>
                  <Text variant="headingXl">$9<Text as="span" variant="bodySm">/month</Text></Text>
                  <Divider />
                  <List>
                    <List.Item>✓ Unlimited label printing</List.Item>
                    <List.Item>✓ Bulk print up to 1,000 labels</List.Item>
                    <List.Item>✓ All printer presets (Dymo, Zebra, Brother)</List.Item>
                    <List.Item>✓ Custom label templates</List.Item>
                    <List.Item>✓ CSV bulk import</List.Item>
                    <List.Item>✓ ZPL support for Zebra printers</List.Item>
                    <List.Item>✓ Priority email support</List.Item>
                  </List>
                </BlockStack>
              </Box>
            </Card>

            <Text variant="bodySm" tone="subdued" alignment="center">
              Billed securely through Shopify. Cancel anytime from your Shopify admin.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}

export default SubscriptionBanner;
